import { createActionGroup, emptyProps, props } from '@ngrx/store';
import {
  FeasibilityEstimate,
  FindSurveysParams,
  FlexibilityPrices,
  RespondentCountsParams,
  SearchCriteriaSurveysParams,
  SearchGroupSurveysParams,
  SimilarSurvey,
  SurveyEpcEstimateQuery,
  SurveySummary,
} from '@ps/survey-utils';
import { SurveyChildKey } from '../survey-creation-shared-state.model';
import { WithScope } from '../survey-creation-child-registry.helper';
import { SurveyCreationPricingState } from './survey-creation-insights-state.model';
import { Nullable } from '@ps/utils';
import { OfferPriceApply } from './utils/survey-creation-apply-offer-price.types';

export const SurveyCreationInsightsActions = createActionGroup({
  source: 'Survey Creation Insights',
  events: {
    'Refresh After Save': emptyProps(),
    'Sync Active Child': props<{ activeChildId: Nullable<number> }>(),
    'Apply Cpi Pricing': props<WithScope<SurveyCreationPricingState>>(),
    'Apply Offer Price': props<OfferPriceApply>(),
    'Resync Similar Surveys': emptyProps(),
    'Search Similar Surveys Outside Account': emptyProps(),
    'Cancel Similar Surveys Search': emptyProps(),
    'Mark Similar Surveys Stale': emptyProps(),
  },
});

export const SurveyCreationInsightsPreDraftActions = createActionGroup({
  source: 'Survey Creation Insights Pre Draft',
  events: {
    'Calculate Feasibility For Pre Draft': emptyProps(),
  },
});

export const SurveyCreationInsightsApiActions = createActionGroup({
  source: 'Survey Creation Insights API',
  events: {
    'Compute Score Pending': emptyProps(),
    'Compute Score Success': props<{
      score: number;
      scorePercentage: number;
    }>(),
    'Compute Score Failure': props<{ error: string }>(),
    'Calculate Feasibility Pending': props<{ key: SurveyChildKey }>(),
    'Calculate Feasibility Success': props<
      FeasibilityEstimate & { key: SurveyChildKey }
    >(),
    'Calculate Feasibility Failure': props<{
      key: SurveyChildKey;
      error: string;
    }>(),
    'Calculate Flexibility Prices Success': props<{
      key: SurveyChildKey;
      prices: FlexibilityPrices;
    }>(),
    'Calculate Flexibility Prices Failure': props<{
      key: SurveyChildKey;
      error: string;
    }>(),
    'Recalculate Feasibility Pending': emptyProps(),
    'Recalculate Feasibility Success': props<FeasibilityEstimate>(),
    'Recalculate Feasibility Failure': props<{ error: string }>(),
    'Load Flexibility Prices Success': props<{ prices: FlexibilityPrices }>(),
    'Load Flexibility Prices Failure': props<{ error: string }>(),
    // Auto PurePrice recompute: the recalculated pure price becomes the new
    // global override (offerPrice + suppliersMetaData.priceOverride).
    'Load Pure Price Cpi Success': props<{ cpi: number }>(),
    'Load Pure Price Cpi Failure': props<{ error: string }>(),
    'Load Similar Surveys Pending': props<{ searchGlobally: boolean }>(),
    'Load Similar Surveys Success': props<{
      surveys: SimilarSurvey[];
      searchGlobally: boolean;
      notify: boolean;
      targetingKey: string;
      isStale: boolean;
    }>(),
    'Load Similar Surveys Failure': props<{ error: string }>(),
    'Find Improbable Quotas Success': props<{ quotaIds: string[] }>(),
    'Find Improbable Quotas Failure': props<{ error: string }>(),
    'Load Epc Estimate Pending': props<{ query: SurveyEpcEstimateQuery }>(),
    'Clear Epc Estimate': emptyProps(),
    'Load Epc Estimate Success': props<{ estimate: number }>(),
    'Load Epc Estimate Failure': props<{ error: string }>(),
    'Load Epc Platform Success': props<{
      countryCode: string;
      platform: number;
    }>(),
    'Load Epc Platform Failure': props<{ error: string }>(),
    'Load Epc Actual Success': props<{ actual: number }>(),
    'Load Epc Actual Failure': props<{ error: string }>(),
  },
});

export const SurveyCreationInsightsPreDraftApiActions = createActionGroup({
  source: 'Survey Creation Insights Pre Draft API',
  events: {
    'Calculate Feasibility For Pre Draft Pending': props<{
      key: SurveyChildKey;
    }>(),
    'Calculate Feasibility For Pre Draft Success': props<
      FeasibilityEstimate & { key: SurveyChildKey }
    >(),
    'Calculate Feasibility For Pre Draft Failure': props<{
      key: SurveyChildKey;
      error: string;
    }>(),
  },
});

export const AdvancedFilterActions = createActionGroup({
  source: 'Survey Creation Advanced Filters',
  events: {
    'Find Surveys': props<{ params: FindSurveysParams }>(),
    'Get Respondent Counts': props<{ params: RespondentCountsParams }>(),
    'Search Criteria Surveys': props<{ params: SearchCriteriaSurveysParams }>(),
    'Search Group Surveys': props<{ params: SearchGroupSurveysParams }>(),
    // Survey Options are a temporary list: typeahead results are dropped once a field is done with them.
    'Clear Survey Options': emptyProps(),
    'Reset Filters': emptyProps(),
  },
});

export const AdvancedFilterApiActions = createActionGroup({
  source: 'Survey Creation Advanced Filters API',
  events: {
    'Find Surveys Success': props<{
      surveys: SurveySummary[];
      psidRefsBySurveyId: Record<number, string>;
    }>(),
    'Find Surveys Failure': props<{ error: string }>(),
    'Respondent Counts Success': props<{ totalCount: number }>(),
    'Respondent Counts Failure': props<{ error: string }>(),
    'Search Criteria Surveys Success': props<{ surveys: SurveySummary[] }>(),
    'Search Criteria Surveys Failure': props<{ error: string }>(),
    'Search Group Surveys Success': props<{
      surveys: SurveySummary[];
      psidRefsBySurveyId: Record<number, string>;
    }>(),
    'Search Group Surveys Failure': props<{ error: string }>(),
  },
});
